<template>
  <button class="Button" type="button" :class="[size, mode, platform, { hover, focus, hasIcon }]" :style="{ width }" :disabled="disabled || loading">
    <div class="ButtonInner">
      <div class="ButtonIcon" v-if="hasIcon && !loading">
        <slot name="icon"></slot>
      </div>
      <div class="ButtonLoading" v-if="loading">
        <span></span>
      </div>
      <span class="ButtonText">{{ text }}</span>
    </div>
  </button>
</template>

<script lang="ts">
import { defineComponent, computed, useSlots, onMounted } from 'vue'

export default defineComponent({
  props: {
    size: {
      type: String as () => 'x-sm' | 'sm' | 'md' | 'lg',
      default: 'md'
    },
    platform: {
      type: String as () => 'common' | 'sell' | 'buy',
      default: 'common'
    },
    mode: {
      type: String as () => 'primary' | 'secondary' | 'tertiary' | 'distructive',
      default: 'primary'
    },
    hover: {
      type: Boolean,
      default: false
    },
    focus: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      required: true
    },
    width: {
      type: String,
      default: 'auto'
    }
  },
  setup(props) {
    const slots = useSlots()

    const hasIcon = computed<boolean>(() => !!slots.icon)

    return {
      hasIcon
    }
  }
})
</script>

<style scoped>
.Button {
  appearance: none;
  border-radius: 8px;
  border: none;
  box-shadow: var(--elevation-shadow-100);
  cursor: pointer;
  transition: .3s;
  padding: 0 12px;
}

.Button:disabled {
  cursor: not-allowed;
}

.Button.common {
  color: var(--primitive-colors-neutral-black-700);
}

.Button.sell.primary, .Button.buy.primary {
  color: var(--semantic-colors-text-white);
}

.Button.sell.primary .ButtonIcon > *, :slotted(.Button.sell.primary .ButtonIcon > *),
.Button.buy.primary .ButtonIcon > *, :slotted(.Button.buy.primary .ButtonIcon > *) {
  fill: var(--semantic-colors-text-white);
}

.Button.sell.secondary .ButtonIcon > *, :slotted(.Button.sell.secondary .ButtonIcon > *) {
  position: relative;
  fill: var(--primitive-colors-basic-pinkred-400);
  z-index: 1;
}

.Button.buy.secondary .ButtonIcon > *, :slotted(.Button.buy.secondary .ButtonIcon > *) {
  position: relative;
  fill: var(--primitive-colors-basic-lightblue-500);
  z-index: 1;
}

.Button.hasIcon {
  padding-left: 8px;
}

.ButtonInner {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.ButtonIcon {
  width: 20px;
  height: 20px;
}

.ButtonLoading {
  width: 20px;
  height: 20px;
}

.ButtonIcon > *, :slotted(.ButtonIcon > *) {
  width: 100%;
  height: 100%;
}

.ButtonText {
  font-size: var(--font-size-body-2);
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.13px;
}

.Button.sm .ButtonText {
  font-size: var(--font-size-capture-1);
}

.Button.x-sm .ButtonText {
  font-size: var(--font-size-capture-2);
  letter-spacing: 0.11px;
}

.Button.lg {
  min-width: 295px;
  height: 42px;
}

.Button.md {
  min-width: 143px;
  height: 42px;
}

.Button.sm {
  min-width: 100px;
  height: 38px;
}

.Button.x-sm {
  min-width: 65px;
  height: 32px;
}

.Button.common.primary {
  background: var(--primitive-colors-gradient-yellow-500);
}

.Button.sell.primary {
  background: var(--primitive-colors-gradient-pink-500);
}

.Button.sell.secondary {
  color: var(--primitive-colors-basic-pinkred-400);
  position: relative;
  background: var(--primitive-colors-gradient-pink-500);
}

.Button.buy.primary {
  background: var(--primitive-colors-gradient-blue-500);
}

.Button.buy.secondary {
  color: var(--primitive-colors-basic-lightblue-500);
  position: relative;
  background: var(--primitive-colors-gradient-blue-500);
}

.Button.sell.secondary::after,
.Button.buy.secondary::after {
  content: "";
  border-radius: 7px;
  position: absolute;
  display: inline-block;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  background: var(--primitive-colors-neutral-white-400);
  left: 1px;
  top: 1px;
}

.Button.sell.secondary .ButtonText,
.Button.buy.secondary .ButtonText {
  position: relative;
  z-index: 1;
}

.Button.common.primary:not([disabled]):hover,
.Button.common.primary.hover {
  background: var(--primitive-colors-gradient-yellow-600);
}

.Button.sell.primary:not([disabled]):hover,
.Button.sell.primary.hover {
  background: var(--primitive-colors-gradient-pink-600);
}

.Button.sell.secondary:not([disabled]):hover::after,
.Button.sell.secondary.hover::after {
  content: "";
  border-radius: 7px;
  position: absolute;
  display: inline-block;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  background: var(--primitive-colors-basic-pinkred-100);
  left: 1px;
  top: 1px;
}

.Button.buy.primary:not([disabled]):hover,
.Button.buy.primary.hover {
  background: var(--primitive-colors-gradient-blue-600);
}

.Button.buy.secondary:not([disabled]):hover::after,
.Button.buy.secondary.hover::after {
  content: "";
  border-radius: 7px;
  position: absolute;
  display: inline-block;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  background: var(--primitive-colors-basic-lightblue-100);
  left: 1px;
  top: 1px;
}

.Button.common.primary:focus,
.Button.common.primary.focus {
  background: var(--primitive-colors-gradient-yellow-500);
  outline: solid 3px var(--primitive-colors-basic-yellow-200);
}

.Button.sell.primary:focus,
.Button.sell.primary.focus {
  background: var(--primitive-colors-gradient-pink-500);
  outline: solid 3px var(--primitive-colors-basic-pinkred-200);
  animation: focus-sell .3s ease;
}

.Button.sell.secondary:focus,
.Button.sell.secondary.focus {
  outline: solid 3px var(--primitive-colors-basic-pinkred-200);
  animation: focus-sell .3s ease;
}

.Button.buy.primary:focus,
.Button.buy.primary.focus {
  background: var(--primitive-colors-gradient-blue-500);
  outline: solid 3px var(--primitive-colors-basic-lightblue-200);
  animation: focus-buy .3s ease;
}

.Button.buy.secondary:focus,
.Button.buy.secondary.focus {
  outline: solid 3px var(--primitive-colors-basic-lightblue-200);
  animation: focus-buy .3s ease;
}

.Button.common:disabled {
  background: var(--primitive-colors-gradient-yellow-200);
  color: var(--semantic-colors-text-disabled);
}

.Button.sell:disabled {
  background: var(--primitive-colors-gradient-pink-200);
}

.Button.sell.secondary:disabled {
  color: var(--primitive-colors-basic-pinkred-200);
  position: relative;
  background: var(--primitive-colors-gradient-pink-200);
}

.Button.buy:disabled {
  background: var(--primitive-colors-gradient-blue-200);
}

.Button.buy.secondary:disabled {
  color: var(--primitive-colors-basic-lightblue-200);
  position: relative;
  background: var(--primitive-colors-gradient-blue-200);
}

.Button.common:disabled .ButtonIcon > *, :slotted(.Button.common:disabled .ButtonIcon > *),
.Button.tertiary:disabled .ButtonIcon > *, :slotted(.Button.tertiary:disabled .ButtonIcon > *) {
  fill: var(--semantic-colors-text-disabled);
}

.Button.sell.secondary:disabled .ButtonIcon > *, :slotted(.Button.sell.secondary:disabled .ButtonIcon > *) {
  position: relative;
  fill: var(--primitive-colors-basic-pinkred-200);
  z-index: 1;
}

.Button.buy.secondary:disabled .ButtonIcon > *, :slotted(.Button.buy.secondary:disabled .ButtonIcon > *) {
  position: relative;
  fill: var(--primitive-colors-basic-lightblue-200);
  z-index: 1;
}

.Button.common.secondary {
  background: var(--primitive-colors-neutral-white-500);
  border: 1px solid var(--semantic-colors-border-box);
}

.Button.common.secondary:not([disabled]):hover,
.Button.common.secondary.hover {
  background: var(--primitive-colors-neutral-white-600);
}

.Button.common.secondary:focus,
.Button.common.secondary.focus {
  background: var(--primitive-colors-neutral-white-500);
  outline: solid 1px var(--semantic-colors-border-box);
}

.Button.tertiary {
  background: transparent;
  box-shadow: none;
  border-radius: 0;
}

.Button.tertiary .ButtonText {
  font-weight: 400;
}

.Button.tertiary:not([disabled]):hover,
.Button.tertiary.hover {
  background: var(--primitive-colors-neutral-white-300a);
}

.Button.tertiary:focus,
.Button.tertiary.focus {
  outline: solid 3px var(--semantic-colors-border-section);
}

.Button.tertiary:disabled {
  background: transparent;
  color: var(--semantic-colors-text-disabled);
}

.Button.distructive {
  background: var(--primitive-colors-system-red-500);
}

.Button.distructive .ButtonIcon > *, :slotted(.Button.distructive .ButtonIcon > *) {
  fill: var(--semantic-colors-text-white);
}

.Button.distructive .ButtonText {
  color: var(--semantic-colors-text-white);
}

.Button.distructive:not([disabled]):hover,
.Button.distructive.hover {
  background: var(--primitive-colors-system-red-600);
}

.Button.distructive:focus,
.Button.distructive.focus {
  outline: solid 3px var(--primitive-colors-system-red-200);
}

.Button.distructive:disabled {
  background: var(--primitive-colors-system-red-200);
}

.Button.distructive:disabled .ButtonIcon > *, :slotted(.Button.distructive:disabled .ButtonIcon > *) {
  fill: var(--semantic-colors-text-white);
}

.ButtonLoading span {
  position: relative;
  display: inline-block;
  width: 90%;
  height: 90%;
  border: 2px solid var(--primitive-colors-neutral-black-100a);
  border-top: 2px solid var(--primitive-colors-neutral-black-800);
  border-radius: 50%;
  animation: loading 1.0s infinite;
}

.Button.distructive .ButtonLoading span,
.Button.sell.primary .ButtonLoading span,
.Button.buy.primary .ButtonLoading span {
  border: 2px solid var(--primitive-colors-neutral-white-200a);
  border-top: 2px solid var(--primitive-colors-neutral-white-400);
}

.Button.sell.secondary .ButtonLoading span {
  position: relative;
  z-index: 1;
  border: 2px solid var(--primitive-colors-basic-pinkred-200);
  border-top: 2px solid var(--primitive-colors-basic-pinkred-500);
}

.Button.buy.secondary .ButtonLoading span {
  position: relative;
  z-index: 1;
  border: 2px solid var(--primitive-colors-basic-lightblue-200);
  border-top: 2px solid var(--primitive-colors-basic-lightblue-500);
}

@keyframes loading {
  0% {
    transform: rotate(45deg);
  }
  100% { 
    transform: rotate(405deg);
  }
}

@keyframes focus-sell {
  0% {
    border: 1px solid var(--primitive-colors-basic-pinkred-700);
    outline: solid 0px var(--primitive-colors-basic-pinkred-700);
  }
  50% {
    border: none;
    outline: solid 3px var(--primitive-colors-basic-pinkred-300);
  }
  100% {
    border: none;
    outline: solid 3px var(--primitive-colors-basic-pinkred-200);
  }
}

@keyframes focus-buy {
  0% {
    border: 1px solid var(--primitive-colors-basic-lightblue-700);
    outline: solid 0px var(--primitive-colors-basic-lightblue-700);
  }
  50% {
    border: none;
    outline: solid 3px var(--primitive-colors-basic-lightblue-300);
  }
  100% {
    border: none;
    outline: solid 3px var(--primitive-colors-basic-lightblue-200);
  }
}
</style>